import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import BiotechOutlinedIcon from "@mui/icons-material/BiotechOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import { useNavigate } from "react-router-dom";

import Header from "../../components/Header";
import { authGet, authPost, authPatch, unwrapRows } from "../../lib/authApi";

const emptyForm = {
  org_id: "",
  patient_id: "",
  provider_id: "",
  facility_id: "",
  department_id: "",
  test_type: "TOX",
  specimen_source: "Urine",
  sample_type: "Urine",
  medications: "",
  diagnosis_codes: "",
  notes: "",
  collected_at: "",
  received_at: "",
};

const normalizeStatus = (value) => String(value || "")
  .trim()
  .toLowerCase()
  .replace(/[\s-]+/g, "_");

const statusColor = (s) => {
  const k = normalizeStatus(s);
  if (["released", "final"].includes(k)) return "success";
  if (["result_imported", "lab_results"].includes(k)) return "info";
  if (["received", "waiting_list", "ordered"].includes(k)) return "warning";
  if (k === "cancelled") return "error";
  return "default";
};

const statusLabel = (row) => {
  if (row?.report_released_at || normalizeStatus(row?.status) === "released") return "Released";
  return ({
    received: "Received",
    waiting_list: "Waiting list",
    ordered: "Ordered",
    accessioned: "Accessioned",
    result_imported: "Results imported",
    lab_results: "Lab results",
    final: "Final — unreleased",
  })[normalizeStatus(row?.status)] || row?.status || "Ordered";
};

const formatDate = (value) => value ? new Date(value).toLocaleString() : "—";

// Lifecycle choice is presentation-only. The server remains authoritative for every transition.
const lifecycleFor = (row) => {
  const status = normalizeStatus(row?.status);
  if (row?.report_released_at || status === "released") return { kind: "released", label: "Released" };
  if (["received", "waiting_list", "ordered"].includes(status)) return { kind: "accession", label: "Receive & accession" };
  if (status === "accessioned") return { kind: "label", label: "Print barcode label" };
  if (["result_imported", "lab_results"].includes(status)) return { kind: "review", label: "Review results" };
  if (status === "final") return { kind: "release", label: "Release to Account" };
  return null;
};

const patientLabel = (p) =>
  p?.full_name || [p?.first_name, p?.last_name].filter(Boolean).join(" ") || p?.patient_name || "—";

const providerLabel = (p) => [p?.first_name, p?.last_name].filter(Boolean).join(" ") || "—";

const inlineForms = {
  patient: { first_name: "", last_name: "", dob: "", gender: "", mrn: "", patient_external_id: "" },
  provider: { first_name: "", last_name: "", npi: "", phone: "" },
  facility: { name: "", address1: "", city: "", state: "", zip: "", phone: "" },
};

export default function Intake({ view = "waiting-list" }) {
  const navigate = useNavigate();
  const isNewOrder = view === "new-order";
  const [form, setForm] = useState(emptyForm);
  const [orgs, setOrgs] = useState([]);
  const [patients, setPatients] = useState([]);
  const [providers, setProviders] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [createKind, setCreateKind] = useState("");
  const [inlineForm, setInlineForm] = useState(inlineForms.patient);
  const [inlineError, setInlineError] = useState("");
  const [inlineSaving, setInlineSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, msg: "", severity: "success" });
  const [selectedIds, setSelectedIds] = useState([]);
  const [detailOrder, setDetailOrder] = useState(null);

  const set = (key) => (e) => setForm((s) => ({ ...s, [key]: e.target.value }));
  const setInline = (key) => (e) => setInlineForm((s) => ({ ...s, [key]: e.target.value }));

  async function loadOrgs() {
    const resp = await authGet("/organizations");
    const rows = unwrapRows(resp);
    setOrgs(rows);
    const saved = localStorage.getItem("org_id");
    const nextOrg = saved && rows.some((o) => o.id === saved) ? saved : rows[0]?.id || "";
    setForm((s) => ({ ...s, org_id: s.org_id || nextOrg }));
  }

  async function loadOrgData(orgId) {
    if (!orgId) return;
    setLoading(true);
    try {
      const [pat, prov, fac, dept, ord] = await Promise.all([
        authGet(`/patients?org_id=${encodeURIComponent(orgId)}&limit=5000`),
        authGet(`/providers?org_id=${encodeURIComponent(orgId)}`),
        authGet(`/facilities?org_id=${encodeURIComponent(orgId)}&limit=5000`),
        authGet(`/departments/active?org_id=${encodeURIComponent(orgId)}`),
        authGet(`/test-orders?org_id=${encodeURIComponent(orgId)}&limit=500`),
      ]);
      setPatients(unwrapRows(pat));
      setProviders(unwrapRows(prov));
      setFacilities(unwrapRows(fac));
      setDepartments(unwrapRows(dept));
      setOrders(unwrapRows(ord));
    } catch (e) {
      setToast({ open: true, msg: e.message || "Failed to load intake data.", severity: "error" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadOrgs().catch((e) => setToast({ open: true, msg: e.message || "Failed to load organizations.", severity: "error" }));
  }, []);

  useEffect(() => {
    if (form.org_id) {
      localStorage.setItem("org_id", form.org_id);
      loadOrgData(form.org_id);
    }
  }, [form.org_id]);

  const selectedPatient = useMemo(() => patients.find((p) => p.id === form.patient_id), [patients, form.patient_id]);

  async function refreshOrders() {
    if (!form.org_id) return;
    const resp = await authGet(`/test-orders?org_id=${encodeURIComponent(form.org_id)}&limit=500`);
    setOrders(unwrapRows(resp));
  }

  function openInlineCreate(kind) {
    if (!form.org_id) {
      setToast({ open: true, msg: "Select an organization before adding a record.", severity: "error" });
      return;
    }
    setInlineError("");
    setInlineSaving(false);
    setInlineForm(inlineForms[kind]);
    setCreateKind(kind);
  }

  function closeInlineCreate() {
    if (!inlineSaving) setCreateKind("");
  }

  async function createInlineRecord() {
    if (!createKind || !form.org_id) return;
    const required = createKind === "patient"
      ? ["first_name", "last_name"]
      : createKind === "provider"
        ? ["first_name", "last_name"]
        : ["name"];
    if (required.some((key) => !String(inlineForm[key] || "").trim())) {
      setInlineError(createKind === "facility" ? "Facility name is required." : "First name and last name are required.");
      return;
    }

    const endpoint = createKind === "patient" ? "/patients" : createKind === "provider" ? "/providers" : "/facilities";
    setInlineSaving(true);
    setInlineError("");
    try {
      const payload = Object.fromEntries(Object.entries(inlineForm).map(([key, value]) => [key, typeof value === "string" ? value.trim() : value]));
      const response = await authPost(endpoint, { ...payload, org_id: form.org_id });
      const record = response?.[createKind] || response;
      if (!record?.id) throw new Error(`The ${createKind} was created but no record was returned.`);

      if (createKind === "patient") {
        setPatients((rows) => [...rows, record].sort((a, b) => patientLabel(a).localeCompare(patientLabel(b))));
        setForm((s) => ({ ...s, patient_id: record.id }));
      } else if (createKind === "provider") {
        setProviders((rows) => [...rows, record].sort((a, b) => providerLabel(a).localeCompare(providerLabel(b))));
        setForm((s) => ({ ...s, provider_id: record.id }));
      } else {
        setFacilities((rows) => [...rows, record].sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))));
        setForm((s) => ({ ...s, facility_id: record.id }));
      }
      setCreateKind("");
      setToast({ open: true, msg: `${createKind[0].toUpperCase()}${createKind.slice(1)} added and selected for this order.`, severity: "success" });
    } catch (e) {
      setInlineError(e.message || `Failed to add ${createKind}.`);
    } finally {
      setInlineSaving(false);
    }
  }

  const inlineTitle = createKind ? `Add ${createKind[0].toUpperCase()}${createKind.slice(1)}` : "";

  async function createOrder() {
    if (!form.org_id) return setToast({ open: true, msg: "Select an organization.", severity: "error" });
    if (!form.patient_id) return setToast({ open: true, msg: "Select a patient before creating an order.", severity: "error" });

    setBusy(true);
    try {
      const payload = {
        ...form,
        provider_id: form.provider_id || null,
        facility_id: form.facility_id || null,
        department_id: form.department_id || null,
        collected_at: form.collected_at || null,
        received_at: form.received_at || null,
      };
      const resp = await authPost("/test-orders", payload);
      const accession = resp?.order?.accession_number || "created";
      setToast({ open: true, msg: `Order ${accession} created and staged.`, severity: "success" });
      setForm((s) => ({ ...emptyForm, org_id: s.org_id, test_type: "TOX", specimen_source: "Urine", sample_type: "Urine" }));
      if (isNewOrder) {
        navigate("/intake");
      } else {
        await refreshOrders();
      }
    } catch (e) {
      setToast({ open: true, msg: e.message || "Failed to create order.", severity: "error" });
    } finally {
      setBusy(false);
    }
  }

  async function updateStatus(row, status) {
    setBusy(true);
    try {
      await authPatch(`/test-orders/${encodeURIComponent(row.id)}/status`, { org_id: form.org_id, status });
      setToast({ open: true, msg: `${row.accession_number} marked ${status}.`, severity: "success" });
      await refreshOrders();
    } catch (e) {
      setToast({ open: true, msg: e.message || "Failed to update status.", severity: "error" });
    } finally {
      setBusy(false);
    }
  }

  async function releaseToAccount(row) {
    setBusy(true);
    try {
      await authPost(`/account-reports/orders/${encodeURIComponent(row.id)}/release`, {});
      setToast({ open: true, msg: `${row.accession_number || "Order"} released to the Account.`, severity: "success" });
      await refreshOrders();
    } catch (e) {
      setToast({ open: true, msg: e.message || "Failed to release the report to the Account.", severity: "error" });
    } finally {
      setBusy(false);
    }
  }

  // All patient/order data is assigned through textContent; no untrusted value enters HTML markup.
  function printLabels(rows) {
    const printableRows = rows.filter((row) => lifecycleFor(row)?.kind === "label");
    if (!printableRows.length) return;
    const win = window.open("", "_blank");
    if (!win) return;
    const { document: doc } = win;
    doc.title = printableRows.length === 1 ? (printableRows[0].accession_number || "Barcode label") : "Barcode labels";
    const style = doc.createElement("style");
    style.textContent = "body{font-family:Arial,sans-serif;padding:16px}.toolbar{margin-bottom:16px}.label{width:320px;border:1px solid #111;padding:12px;border-radius:8px;margin:0 0 12px;break-inside:avoid}.acc{font-size:22px;font-weight:700;letter-spacing:.5px}.muted{color:#555;font-size:12px;margin-top:4px}@media print{.toolbar{display:none}.label{page-break-after:always}}";
    doc.head.append(style);
    const toolbar = doc.createElement("div");
    toolbar.className = "toolbar";
    const printButton = doc.createElement("button");
    printButton.type = "button";
    printButton.textContent = "Print";
    printButton.addEventListener("click", () => win.print());
    toolbar.append(printButton);
    doc.body.append(toolbar);
    printableRows.forEach((row) => {
      const label = doc.createElement("section");
      label.className = "label";
      const accession = doc.createElement("div");
      accession.className = "acc";
      accession.textContent = row.accession_number || "—";
      const patient = doc.createElement("div");
      patient.textContent = row.patient_name || "—";
      const specimen = doc.createElement("div");
      specimen.className = "muted";
      specimen.textContent = `${row.test_type || "TOX"} • ${row.specimen_source || row.sample_type || "—"}`;
      const patientId = doc.createElement("div");
      patientId.className = "muted";
      patientId.textContent = `Patient ID: ${row.patient_external_id || "—"}`;
      label.append(accession, patient, specimen, patientId);
      doc.body.append(label);
    });
    doc.close();
  }

  const selectedOrders = useMemo(() => orders.filter((row) => selectedIds.includes(row.id)), [orders, selectedIds]);
  const canBatchPrint = selectedOrders.length > 0 && selectedOrders.every((row) => lifecycleFor(row)?.kind === "label");

  const columns = [
    { field: "accession_number", headerName: "Accession", width: 180 },
    { field: "patient_name", headerName: "Patient", flex: 1, minWidth: 180 },
    { field: "test_type", headerName: "Test", width: 90 },
    { field: "facility_name", headerName: "Facility", flex: 1, minWidth: 150, valueGetter: (_value, row) => row?.facility_name || "—" },
    { field: "provider_name", headerName: "Provider", flex: 1, minWidth: 150, valueGetter: (_value, row) => row?.provider_name || "—" },
    {
      field: "status",
      headerName: "Status",
      width: 145,
      renderCell: ({ row }) => <Chip size="small" color={statusColor(row.status)} label={statusLabel(row)} aria-label={`Order status: ${statusLabel(row)}`} />,
    },
    { field: "created_at", headerName: "Ordered", width: 175, valueGetter: (_value, row) => formatDate(row?.created_at) },
    { field: "accessioned_at", headerName: "Accessioned", width: 175, valueGetter: (_value, row) => formatDate(row?.accessioned_at) },
    {
      field: "actions",
      headerName: "Actions",
      width: 390,
      sortable: false,
      renderCell: ({ row }) => {
        const lifecycle = lifecycleFor(row);
        return <Stack direction="row" spacing={0.5} alignItems="center">
          {lifecycle?.kind === "accession" && <Button size="small" variant="contained" onClick={() => updateStatus(row, "accessioned")} disabled={busy}>Receive & accession</Button>}
          {lifecycle?.kind === "label" && <Button size="small" variant="contained" startIcon={<LocalPrintshopOutlinedIcon />} onClick={() => printLabels([row])} disabled={busy}>Print barcode label</Button>}
          {lifecycle?.kind === "review" && <Button size="small" variant="contained" startIcon={<BiotechOutlinedIcon />} onClick={() => navigate(`/lab-results?order_id=${encodeURIComponent(row.id)}`)} disabled={busy}>Review results</Button>}
          {lifecycle?.kind === "release" && <Button size="small" variant="contained" onClick={() => releaseToAccount(row)} disabled={busy}>Release to Account</Button>}
          {lifecycle?.kind === "released" && <Chip size="small" color="success" label="Released" />}
          <Button size="small" variant="text" onClick={() => setDetailOrder(row)}>View details</Button>
        </Stack>;
      },
    },
  ];

  return (
    <Box p={3} sx={{ pb: 6 }}>
      <Header
        title={isNewOrder ? "New Test Order" : "Waiting List"}
        subtitle={isNewOrder ? "Receive a new test order into the Waiting List; a unique accession and barcode are issued only when its sample is received." : "Receive samples, issue immutable accessions, print labels, and progress results."}
      />

      <Grid container spacing={2}>
        <Grid item xs={12} lg={12} sx={{ display: isNewOrder ? "flex" : "none" }}>
          <Card sx={{ width: "100%", height: { lg: "calc(100vh - 190px)" }, display: "flex", flexDirection: "column" }}>
            <CardContent sx={{ flex: 1, minHeight: 0, overflowY: { lg: "auto" }, pr: { lg: 1.5 } }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <AssignmentTurnedInOutlinedIcon />
                <Typography variant="h5">New Test Order</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Add a new patient, provider, or facility here without leaving New Test Order. New records are also available on their respective pages.
              </Typography>

              <Stack spacing={2}>
                <TextField select label="Organization" value={form.org_id} onChange={set("org_id")} fullWidth>
                  {orgs.map((o) => <MenuItem key={o.id} value={o.id}>{o.name || o.id}</MenuItem>)}
                </TextField>

                <TextField select required label="Patient" value={form.patient_id} onChange={set("patient_id")} fullWidth helperText={selectedPatient ? `Patient ID: ${selectedPatient.patient_external_id || "—"}` : "Select a patient or add one without leaving Intake."}>
                  {patients.map((p) => <MenuItem key={p.id} value={p.id}>{patientLabel(p)} {p.patient_external_id ? `(${p.patient_external_id})` : ""}</MenuItem>)}
                </TextField>
                <Button variant="outlined" onClick={() => openInlineCreate("patient")}>Add New Patient</Button>

                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <TextField select label="Test Type" value={form.test_type} onChange={set("test_type")} fullWidth>
                      <MenuItem value="TOX">TOX</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField select label="Department" value={form.department_id} onChange={set("department_id")} fullWidth>
                      <MenuItem value="">None</MenuItem>
                      {departments.map((d) => <MenuItem key={d.id} value={d.id}>{d.code} — {d.name}</MenuItem>)}
                    </TextField>
                  </Grid>
                </Grid>

                <TextField select label="Provider" value={form.provider_id} onChange={set("provider_id")} fullWidth>
                  <MenuItem value="">None</MenuItem>
                  {providers.map((p) => <MenuItem key={p.id} value={p.id}>{providerLabel(p)} {p.npi ? `(${p.npi})` : ""}</MenuItem>)}
                </TextField>
                <Button variant="outlined" onClick={() => openInlineCreate("provider")}>Add New Provider</Button>

                <TextField select label="Facility" value={form.facility_id} onChange={set("facility_id")} fullWidth>
                  <MenuItem value="">None</MenuItem>
                  {facilities.map((f) => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}
                </TextField>
                <Button variant="outlined" onClick={() => openInlineCreate("facility")}>Add New Facility</Button>

                <Grid container spacing={1}>
                  <Grid item xs={6}><TextField label="Specimen Source" value={form.specimen_source} onChange={set("specimen_source")} fullWidth /></Grid>
                  <Grid item xs={6}><TextField label="Sample Type" value={form.sample_type} onChange={set("sample_type")} fullWidth /></Grid>
                </Grid>

                <TextField label="Medications" value={form.medications} onChange={set("medications")} fullWidth multiline minRows={2} />
                <TextField label="Notes" value={form.notes} onChange={set("notes")} fullWidth multiline minRows={2} />

                <Divider />
                <Button variant="contained" onClick={createOrder} disabled={busy || loading} startIcon={<AssignmentTurnedInOutlinedIcon />}>
                  Create Test Order / Accession
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={12} sx={{ display: isNewOrder ? "none" : "block" }}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Box>
                  <Typography variant="h5">Waiting List</Typography>
                  <Typography variant="body2" color="text.secondary">Results uploads should match these orders by accession number.</Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" startIcon={<AssignmentTurnedInOutlinedIcon />} onClick={() => navigate("/new-test-order")}>New Test Order</Button>
                  <Button variant="outlined" startIcon={<LocalPrintshopOutlinedIcon />} onClick={() => printLabels(selectedOrders)} disabled={!canBatchPrint || busy}>
                    Batch print labels{selectedOrders.length ? ` (${selectedOrders.length})` : ""}
                  </Button>
                  <Button variant="outlined" onClick={refreshOrders} disabled={loading}>Refresh</Button>
                  <Button variant="outlined" startIcon={<LocalShippingOutlinedIcon />} onClick={() => navigate("/tracking")}>Tracking</Button>
                </Stack>
              </Stack>

              <Box height="66vh">
                <DataGrid rows={orders} columns={columns} loading={loading} checkboxSelection rowSelectionModel={selectedIds} onRowSelectionModelChange={(next) => setSelectedIds(Array.from(next))} pageSizeOptions={[25, 50, 100]} initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} disableRowSelectionOnClick />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={Boolean(detailOrder)} onClose={() => setDetailOrder(null)} fullWidth maxWidth="sm" aria-labelledby="order-details-title">
        <DialogTitle id="order-details-title">Order details</DialogTitle>
        <DialogContent dividers>
          {detailOrder && <Stack spacing={1}>
            <Typography><strong>Accession:</strong> {detailOrder.accession_number || "Not accessioned"}</Typography>
            <Typography><strong>Patient:</strong> {detailOrder.patient_name || "—"}</Typography>
            <Typography><strong>Patient ID:</strong> {detailOrder.patient_external_id || "—"}</Typography>
            <Typography><strong>Test:</strong> {detailOrder.test_type || "—"}</Typography>
            <Typography><strong>Status:</strong> {statusLabel(detailOrder)}</Typography>
            <Typography><strong>Ordered:</strong> {formatDate(detailOrder.created_at)}</Typography>
            <Typography><strong>Received:</strong> {formatDate(detailOrder.received_at || detailOrder.order_received_at)}</Typography>
            <Typography><strong>Accessioned:</strong> {formatDate(detailOrder.accessioned_at)}</Typography>
            <Typography><strong>Results reported:</strong> {formatDate(detailOrder.report_date)}</Typography>
            <Typography><strong>Released to Account:</strong> {formatDate(detailOrder.report_released_at)}</Typography>
          </Stack>}
        </DialogContent>
        <DialogActions><Button onClick={() => setDetailOrder(null)}>Close</Button></DialogActions>
      </Dialog>

      <Dialog open={Boolean(createKind)} onClose={closeInlineCreate} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Typography variant="overline" color="primary">Intake quick add</Typography>
          <Typography variant="h5">{inlineTitle}</Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          <Box component="form" autoComplete="off" onSubmit={(e) => { e.preventDefault(); createInlineRecord(); }}>
            <Stack spacing={1.5} sx={{ maxWidth: 320, mx: "auto" }}>
              {inlineError && <Alert severity="error">{inlineError}</Alert>}
              {createKind === "patient" && <>
                <TextField required label="First Name" value={inlineForm.first_name || ""} onChange={setInline("first_name")} fullWidth autoFocus inputProps={{ autoComplete: "off" }} />
                <TextField required label="Last Name" value={inlineForm.last_name || ""} onChange={setInline("last_name")} fullWidth inputProps={{ autoComplete: "off" }} />
                <TextField label="Date of Birth" type="date" value={inlineForm.dob || ""} onChange={setInline("dob")} InputLabelProps={{ shrink: true }} fullWidth />
                <TextField select label="Gender" value={inlineForm.gender || ""} onChange={setInline("gender")} fullWidth><MenuItem value="">Not specified</MenuItem><MenuItem value="male">Male</MenuItem><MenuItem value="female">Female</MenuItem><MenuItem value="other">Other</MenuItem></TextField>
                <TextField label="MRN" value={inlineForm.mrn || ""} onChange={setInline("mrn")} fullWidth inputProps={{ autoComplete: "off" }} />
                <TextField label="Patient ID (optional)" value={inlineForm.patient_external_id || ""} onChange={setInline("patient_external_id")} helperText="Leave blank to generate one." fullWidth inputProps={{ autoComplete: "off" }} />
              </>}
              {createKind === "provider" && <>
                <TextField required label="First Name" value={inlineForm.first_name || ""} onChange={setInline("first_name")} fullWidth autoFocus inputProps={{ autoComplete: "off" }} />
                <TextField required label="Last Name" value={inlineForm.last_name || ""} onChange={setInline("last_name")} fullWidth inputProps={{ autoComplete: "off" }} />
                <TextField label="NPI" value={inlineForm.npi || ""} onChange={setInline("npi")} fullWidth inputProps={{ autoComplete: "off" }} />
                <TextField label="Phone" type="tel" value={inlineForm.phone || ""} onChange={setInline("phone")} fullWidth inputProps={{ autoComplete: "off" }} />
              </>}
              {createKind === "facility" && <>
                <TextField required label="Facility Name" value={inlineForm.name || ""} onChange={setInline("name")} fullWidth autoFocus inputProps={{ autoComplete: "off" }} />
                <TextField label="Address" value={inlineForm.address1 || ""} onChange={setInline("address1")} fullWidth inputProps={{ autoComplete: "off" }} />
                <TextField label="City" value={inlineForm.city || ""} onChange={setInline("city")} fullWidth inputProps={{ autoComplete: "off" }} />
                <TextField label="State" value={inlineForm.state || ""} onChange={setInline("state")} fullWidth inputProps={{ autoComplete: "off" }} />
                <TextField label="ZIP" value={inlineForm.zip || ""} onChange={setInline("zip")} fullWidth inputProps={{ autoComplete: "off" }} />
                <TextField label="Phone" type="tel" value={inlineForm.phone || ""} onChange={setInline("phone")} fullWidth inputProps={{ autoComplete: "off" }} />
              </>}
              <button type="submit" style={{ display: "none" }} aria-hidden="true" />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, justifyContent: "center" }}>
          <Button onClick={closeInlineCreate} disabled={inlineSaving}>Cancel</Button>
          <Button variant="contained" onClick={createInlineRecord} disabled={inlineSaving}>{inlineSaving ? "Saving…" : `Save ${createKind}`}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={4500} onClose={() => setToast((s) => ({ ...s, open: false }))}>
        <Alert severity={toast.severity} onClose={() => setToast((s) => ({ ...s, open: false }))}>{toast.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
